import React, { useCallback, useEffect, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import MuiBox from "@mui/material/Box";
import Drawer from "components/common/Drawer";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import { useNotifications } from "hooks/useNotifications";

const CARD_BASE_SX = {
  borderRadius: "24px",
  pl: 2.25,
  pr: 2.5,
  py: 3,
  width: "100%",
  textAlign: "left",
  cursor: "default",
  borderLeft: "4px solid transparent",
  transition: "background-color 0.15s ease, border-color 0.15s ease",
};

const CARD_UNREAD_SX = {
  ...CARD_BASE_SX,
  bgcolor: "#F0F7FF",
  border: "1px solid #D6E8FB",
  borderLeft: "4px solid #027DEE",
};

const CARD_SEEN_SX = {
  ...CARD_BASE_SX,
  bgcolor: "#fff",
  border: "1px solid #E5E7EB",
  borderLeft: "4px solid transparent",
};

const NotificationDrawer = ({ open = false, onClose, onUnreadCountChange }) => {
  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  const {
    notifications,
    unreadCount,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    markLoadedAsSeen,
  } = useNotifications({ enabled: open });

  useEffect(() => {
    if (!open || !onUnreadCountChange) return;
    onUnreadCountChange(unreadCount);
  }, [open, unreadCount, onUnreadCountChange]);

  const handleClose = useCallback(() => {
    markLoadedAsSeen();
    onUnreadCountChange?.(0);
    onClose?.();
  }, [markLoadedAsSeen, onClose, onUnreadCountChange]);

  useEffect(() => {
    if (!open || loading || !hasMore) return undefined;

    const root = listRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { root, rootMargin: "120px", threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, loading, loadingMore, hasMore, loadMore, notifications.length]);

  const badgeCount = unreadCount;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      ModalProps={{
        BackdropProps: {
          sx: { bgcolor: "rgba(0, 0, 0, 0.2)" },
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 531 },
          maxWidth: "100%",
          borderRadius: { xs: 0, sm: "24px 0 0 24px" },
          bgcolor: "#fff",
          boxShadow: "0px 0px 24px rgba(0, 0, 0, 0.12)",
          overflow: "hidden",
        },
      }}
    >
      <Stack sx={{ height: "100%", overflow: "hidden" }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 2,
            pt: 1.5,
            pb: 1,
            flexShrink: 0,
          }}
        >
          <Box
            component="button"
            type="button"
            aria-label="Close notifications"
            onClick={handleClose}
            sx={{
              width: 49,
              height: 49,
              borderRadius: "14px",
              border: "1px solid #C3C3C3",
              bgcolor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              p: 0,
              boxShadow: "0px 4px 7px -1px rgba(0,0,0,0.1)",
              "&:hover": { bgcolor: "#F9FAFB" },
            }}
          >
            <CloseIcon sx={{ fontSize: 22, color: "#374151" }} />
          </Box>

          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 2 }}>
            <Text
              sx={{
                fontWeight: 700,
                fontSize: 20,
                color: "#0D0D0D",
                lineHeight: 1.3,
              }}
            >
              Notifications
            </Text>
            {badgeCount > 0 ? (
              <Box
                sx={{
                  bgcolor: "#027DEE",
                  minWidth: 24,
                  height: 24,
                  borderRadius: "12px",
                  px: 0.75,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: 13,
                    lineHeight: 1,
                  }}
                >
                  {badgeCount > 99 ? "99+" : badgeCount}
                </Text>
              </Box>
            ) : null}
          </Stack>
        </Stack>

        <MuiBox
          ref={listRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: 2.5,
            pb: 3,
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Box
            sx={{
              borderTop: "1px solid #E5E7EB",
              pt: 2,
              pb: 1.5,
            }}
          >
            <Text
              sx={{
                fontWeight: 800,
                fontSize: 13,
                color: "#6B7280",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
              }}
            >
              All Notifications
            </Text>
          </Box>

          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : notifications.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
              <Text sx={{ color: "#6B7280", fontSize: 14 }}>
                No notifications yet
              </Text>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              {notifications.map((item) => {
                const isUnread = !item.isSeen;
                return (
                <Box
                  key={item.id}
                  component={item.actionUrl ? "button" : "div"}
                  type={item.actionUrl ? "button" : undefined}
                  onClick={
                    item.actionUrl
                      ? () => {
                          handleClose();
                          window.location.assign(item.actionUrl);
                        }
                      : undefined
                  }
                  sx={{
                    ...(isUnread ? CARD_UNREAD_SX : CARD_SEEN_SX),
                    cursor: item.actionUrl ? "pointer" : "default",
                    ...(item.actionUrl
                      ? {
                          font: "inherit",
                          color: "inherit",
                          m: 0,
                          "&:hover": {
                            bgcolor: isUnread ? "#E8F2FC" : "#FAFAFA",
                          },
                        }
                      : null),
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      spacing={1}
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      {isUnread ? (
                        <Box
                          aria-hidden
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#027DEE",
                            mt: 1.25,
                            flexShrink: 0,
                          }}
                        />
                      ) : null}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Text
                          sx={{
                            fontWeight: isUnread ? 700 : 600,
                            fontSize: 18,
                            color: isUnread ? "#0D0D0D" : "#4B5563",
                            lineHeight: 1.35,
                            mb: 0.5,
                          }}
                        >
                          {item.title}
                        </Text>
                        {item.desc ? (
                          <Text
                            sx={{
                              fontWeight: 400,
                              fontSize: 15,
                              color: isUnread ? "#4B5563" : "#9CA3AF",
                              lineHeight: 1.4,
                              whiteSpace: "pre-line",
                            }}
                          >
                            {item.desc}
                          </Text>
                        ) : null}
                        {item.ctaLabel ? (
                          <Text
                            sx={{
                              mt: 1,
                              fontWeight: 600,
                              fontSize: 13,
                              color: "#D97706",
                              lineHeight: 1.3,
                            }}
                          >
                            {item.ctaLabel}
                          </Text>
                        ) : null}
                      </Box>
                    </Stack>
                    {item.timestampLabel ? (
                      <Text
                        sx={{
                          fontWeight: isUnread ? 500 : 400,
                          fontSize: 13,
                          color: isUnread ? "#6B7280" : "#9CA3AF",
                          flexShrink: 0,
                          pt: 0.5,
                        }}
                      >
                        {item.timestampLabel}
                      </Text>
                    ) : null}
                  </Stack>
                </Box>
                );
              })}

              {hasMore ? (
                <MuiBox
                  ref={sentinelRef}
                  sx={{ height: 1, width: "100%", flexShrink: 0 }}
                />
              ) : null}

              {loadingMore ? (
                <Stack alignItems="center" sx={{ py: 2 }}>
                  <CircularProgress size={22} />
                </Stack>
              ) : null}
            </Stack>
          )}
        </MuiBox>
      </Stack>
    </Drawer>
  );
};

export default NotificationDrawer;
